/* jshint esversion: 11 */
/* jshint ignore:start */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { BsStars } from "react-icons/bs";
import { FaTrophy } from "react-icons/fa";
import { FiCheckCircle, FiExternalLink, FiGift, FiShoppingBag, FiX, FiZap } from "react-icons/fi";
import { HiOutlineFilm } from "react-icons/hi2";
import { MdOutlineEvent, MdOutlineRestaurant } from "react-icons/md";
import ThemedPageFrame from "./ThemedPageFrame";
import { SHOP_API_BASE_URL, getXsrfTokenCookie } from "../../utils/auth";
import { showTost } from "../../showTost";

const OFFER_TONES = ["pink", "violet", "green", "gold", "cyan", "red"];
const SCRATCH_REVEAL_THRESHOLD = 34;
const SCRATCH_MIN_DRAG_DISTANCE = 145;
const SCRATCH_POPUP_ANIMATION_MS = 280;

const SCRATCH_COVER_PALETTES = {
    pink: {
        start: "#ff9fca",
        mid: "#ffbd66",
        end: "#ffd767",
        ink: "rgba(171, 91, 32, 0.2)",
        shadow: "rgba(130, 50, 116, 0.2)",
    },
    violet: {
        start: "#9d88ff",
        mid: "#c084fc",
        end: "#f0abfc",
        ink: "rgba(75, 37, 140, 0.2)",
        shadow: "rgba(63, 38, 122, 0.24)",
    },
    green: {
        start: "#6ee7b7",
        mid: "#a7f3d0",
        end: "#fde68a",
        ink: "rgba(31, 115, 80, 0.2)",
        shadow: "rgba(17, 94, 89, 0.2)",
    },
    gold: {
        start: "#f6ad55",
        mid: "#fbd38d",
        end: "#facc15",
        ink: "rgba(141, 80, 13, 0.22)",
        shadow: "rgba(154, 92, 20, 0.24)",
    },
    cyan: {
        start: "#67e8f9",
        mid: "#7dd3fc",
        end: "#a5b4fc",
        ink: "rgba(13, 104, 128, 0.22)",
        shadow: "rgba(14, 116, 144, 0.22)",
    },
    red: {
        start: "#fb7185",
        mid: "#fca5a5",
        end: "#fdba74",
        ink: "rgba(153, 27, 27, 0.2)",
        shadow: "rgba(159, 18, 57, 0.22)",
    },
};

const normalizeExternalUrl = (url) => {
    if (!url || typeof url !== "string") return null;

    const trimmedUrl = url.trim();
    if (!trimmedUrl) return null;
    if (/^https?:\/\//i.test(trimmedUrl)) return trimmedUrl;
    if (/^\/\//.test(trimmedUrl)) return `https:${trimmedUrl}`;

    return `https://${trimmedUrl}`;
};

const escapeHtml = (value) =>
    String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

const termsValueToHtml = (value) => {
    if (!value) return "";

    if (Array.isArray(value)) {
        return `<ol>${value.map((item) => `<li>${escapeHtml(typeof item === "object" ? JSON.stringify(item) : item)}</li>`).join("")}</ol>`;
    }

    if (typeof value === "object") {
        return Object.entries(value)
            .map(([key, item]) => {
                const label = key.replace(/_/g, " ");
                const content = termsValueToHtml(item);
                return content ? `<section><h3>${escapeHtml(label)}</h3>${content}</section>` : "";
            })
            .join("");
    }

    const text = String(value);
    if (/<[a-z][\s\S]*>/i.test(text)) return text;

    return text
        .split(/\n+/)
        .filter(Boolean)
        .map((line) => `<p>${escapeHtml(line)}</p>`)
        .join("");
};

const sanitizeTermsHtml = (html) => {
    if (!html || typeof window === "undefined" || !window.DOMParser) return "";

    const documentObject = new window.DOMParser().parseFromString(html, "text/html");
    documentObject
        .querySelectorAll("script, style, iframe, object, embed, link, meta")
        .forEach((node) => node.remove());

    const contentRoot =
        documentObject.querySelector("main") ||
        documentObject.querySelector("[role='main']") ||
        documentObject.body;

    return contentRoot?.innerHTML || "";
};

const getTermsFetchUrls = (url) => {
    if (!url) return [];

    const encodedUrl = encodeURIComponent(url);
    return [
        url,
        `https://api.allorigins.win/raw?url=${encodedUrl}`,
        `https://api.allorigins.win/get?url=${encodedUrl}`,
    ];
};

const extractTermsResponseHtml = (text) => {
    try {
        const parsedResponse = JSON.parse(text);
        return parsedResponse?.contents || parsedResponse?.html || text;
    } catch {
        return text;
    }
};

const getOfferTermsHtml = (offerData) => {
    const offer = offerData.offer || {};
    const candidates = [
        offer.terms_and_conditions,
        offer.termsAndConditions,
        offer.terms,
        offer.tnc,
        offer.tnc_text,
        offer.how_to_use,
        offer.howToUse,
        offer.redemption_instructions,
        offer.redemptionInstructions,
        offer.redemption_steps,
    ];

    return candidates.map(termsValueToHtml).find(Boolean) || "";
};

const DEFAULT_OFFER_ITEMS = [
    {
        id: "movie-pass",
        brand: "Nabhi Movies",
        type: "Gift Card",
        title: "Flat 30% off movie tickets",
        description: "Use on premium seats, weekend shows, and family bookings.",
        value: "30% OFF",
        code: "MOVIE30",
        validity: "Valid till 31 Aug",
        minSpend: "Min order Rs299",
        tone: "pink",
        icon: HiOutlineFilm,
        logoUrl: null,
    },
    {
        id: "event-night",
        brand: "Nabhi Events",
        type: "Voucher",
        title: "Rs150 off live events",
        description: "Best for concerts, stand-up shows, exhibitions, and fests.",
        value: "Rs150 OFF",
        code: "LIVE150",
        validity: "Valid this week",
        minSpend: "Min order Rs799",
        tone: "violet",
        icon: MdOutlineEvent,
        logoUrl: null,
    },
    {
        id: "shopping-cart",
        brand: "Eleven11 Cart",
        type: "Gift Card",
        title: "Shopping reward card",
        description: "Redeem across groceries, essentials, fashion, and more.",
        value: "Rs250",
        code: "CART250",
        validity: "Valid for 45 days",
        minSpend: "Min order Rs999",
        tone: "green",
        icon: FiShoppingBag,
        logoUrl: null,
    },
    {
        id: "food-fest",
        brand: "Food & Cafes",
        type: "Voucher",
        title: "Buy 1 get 1 dining deal",
        description: "Available on selected cafe, snack, and quick-bite partners.",
        value: "BOGO",
        code: "FOODBOGO",
        validity: "Valid today",
        minSpend: "Selected outlets",
        tone: "gold",
        icon: MdOutlineRestaurant,
        logoUrl: null,
    },
    {
        id: "upi-cashback",
        brand: "Nabhi Pay",
        type: "Cashback",
        title: "Instant wallet cashback",
        description: "Pay through Nabhi wallet and unlock instant rewards.",
        value: "Rs75",
        code: "PAY75",
        validity: "Valid till Sunday",
        minSpend: "Min payment Rs399",
        tone: "cyan",
        icon: FiZap,
        logoUrl: null,
    },
    {
        id: "vip-pass",
        brand: "Nabi VIP",
        type: "Reward",
        title: "VIP access upgrade",
        description: "Priority entry and exclusive partner benefits on eligible bookings.",
        value: "VIP",
        code: "VIPNOW",
        validity: "Limited slots",
        minSpend: "Members first",
        tone: "red",
        icon: FiGift,
        logoUrl: null,
    },
];

const mapApiVoucherToOfferCard = (offerData, voucher, index) => {
    const amount = voucher.amount;
    const minOrderAmount = offerData.offer?.amount_restrictions?.minOrderAmount;

    return {
        id: voucher.voucher_id || `${offerData.id}-${voucher.id}-${index}`,
        scratchId: voucher.id || voucher.voucher_id || offerData.id,
        isScratched: Number(voucher.is_skarch) === 1,
        brand: offerData.offer?.title || offerData.reference_id || "Hubble",
        type: offerData.offer?.redemption_type ? "Voucher" : "Gift Card",
        title: offerData.offer?.title || "Hubble Offer",
        description:
            offerData.offer?.brand_description || offerData.offer?.title || "Redeemable gift card",
        value: amount != null ? `Rs${amount}` : "Hubble Voucher",
        code: voucher.card_number || voucher.voucher_id || offerData.reference_id,
        pin: voucher.card_pin,
        validity:
            voucher.valid_till ||
            (offerData.offer?.voucher_expiry_in_months
                ? `Valid for ${offerData.offer.voucher_expiry_in_months} months`
                : "Valid until available"),
        minSpend: minOrderAmount ? `Min order Rs${minOrderAmount}` : "",
        tone: OFFER_TONES[index % OFFER_TONES.length],
        icon: FiGift,
        logoUrl: offerData.offer?.logo_url || offerData.offer?.thumbnail_url || null,
        redeemUrl: normalizeExternalUrl(voucher.product_link),
        termsUrl: normalizeExternalUrl(
            offerData.offer?.tncurl || offerData.offer?.tnc_url || offerData.tncurl || offerData.tnc_url
        ),
        termsHtml: getOfferTermsHtml(offerData),
    };
};

const OfferRewardPanel = ({ offer }) => {
    const Icon = offer.icon || FiGift;
    const hasRedeemUrl = Boolean(offer.redeemUrl);
    const hasTermsUrl = Boolean(offer.termsUrl);
    const hasTerms = Boolean(offer.termsHtml || offer.termsUrl);
    const [isTermsOpen, setIsTermsOpen] = useState(false);
    const [termsState, setTermsState] = useState({
        status: "idle",
        html: "",
        error: "",
    });

    useEffect(() => {
        if (!isTermsOpen) return undefined;

        if (offer.termsHtml) {
            setTermsState({
                status: "ready",
                html: sanitizeTermsHtml(offer.termsHtml),
                error: "",
            });
            return undefined;
        }

        if (!offer.termsUrl) {
            setTermsState({
                status: "error",
                html: "",
                error: "Terms and conditions are not available for this reward.",
            });
            return undefined;
        }

        const controller = new AbortController();
        setTermsState({ status: "loading", html: "", error: "" });

        const loadTerms = async () => {
            const fetchUrls = getTermsFetchUrls(offer.termsUrl);

            for (const fetchUrl of fetchUrls) {
                try {
                    const response = await fetch(fetchUrl, { signal: controller.signal });
                    if (!response.ok) continue;

                    const html = extractTermsResponseHtml(await response.text());
                    const sanitizedHtml = sanitizeTermsHtml(html);
                    if (sanitizedHtml) {
                        setTermsState({ status: "ready", html: sanitizedHtml, error: "" });
                        return;
                    }
                } catch (error) {
                    if (error.name === "AbortError") return;
                }
            }

            setTermsState({
                status: "error",
                html: "",
                error: "Terms data is not available in the popup.",
            });
        };

        loadTerms();

        return () => controller.abort();
    }, [isTermsOpen, offer.termsUrl, offer.termsHtml]);

    const handleRedeem = () => {
        if (!hasRedeemUrl) return;

        const redeemWindow = window.open(offer.redeemUrl, "_blank");
        if (redeemWindow) {
            redeemWindow.opener = null;
            return;
        }

        window.location.href = offer.redeemUrl;
    };

    const handleTermsPageOpen = () => {
        if (!hasTermsUrl) return;

        const termsWindow = window.open(offer.termsUrl, "_blank");
        if (termsWindow) {
            termsWindow.opener = null;
            return;
        }

        window.location.href = offer.termsUrl;
    };

    const handleTermsOpen = () => {
        if (!hasTerms) return;
        setIsTermsOpen(true);
    };

    const shouldShowOffText = offer.value && !/off/i.test(offer.value);
    const rewardType = offer.type || "Reward";

    return (
        <>
            <div className="offers_reward">
                <div className="offers_reward_prize">
                    <div className="offers_reward_brand">
                        {offer.logoUrl ? (
                            <img src={offer.logoUrl} alt={`${offer.brand} logo`} loading="lazy" />
                        ) : (
                            <Icon aria-hidden="true" />
                        )}
                        <div>
                            <span>{offer.brand}</span>
                            <small>{rewardType}</small>
                        </div>
                    </div>

                    <div className="offers_reward_visual">
                        <FaTrophy className="offers_reward_trophy" aria-hidden="true" />
                    </div>

                    <strong>{offer.value}{shouldShowOffText ? <em>OFF</em> : null}</strong>

                    <p>{offer.minSpend || offer.description}</p>

                    {offer.code ? (
                        <div className="offers_reward_code">
                            <span>Use code</span>
                            <strong>{offer.code}</strong>
                        </div>
                    ) : null}
                </div>

                <div className="offers_reward_footer">
                    <button
                        className="offers_terms_button"
                        type="button"
                        onClick={handleTermsOpen}
                        disabled={!hasTerms}
                    >
                        T&amp;C
                    </button>
                    <button
                        className="offers_claim_button"
                        type="button"
                        onClick={handleRedeem}
                        disabled={!hasRedeemUrl}
                    >
                        Claim now
                    </button>
                </div>
            </div>

            {isTermsOpen ? (
                <div
                    className="offers_tnc_modal"
                    role="dialog"
                    aria-modal="true"
                    aria-label={`${offer.brand} terms and conditions`}
                    onClick={() => setIsTermsOpen(false)}
                >
                    <div className="offers_tnc_modal_box" onClick={(event) => event.stopPropagation()}>
                        <button
                            className="offers_tnc_modal_close"
                            type="button"
                            aria-label="Close terms and conditions"
                            onClick={() => setIsTermsOpen(false)}
                        >
                            <FiX aria-hidden="true" />
                        </button>
                        <div className="offers_tnc_modal_header">
                            <div className="offers_tnc_brand_media">
                                {offer.logoUrl ? (
                                    <img src={offer.logoUrl} alt={`${offer.brand} logo`} loading="lazy" />
                                ) : (
                                    <Icon aria-hidden="true" />
                                )}
                            </div>
                            <h2>{offer.brand}</h2>
                        </div>
                        <div className="offers_tnc_modal_body">
                            {termsState.status === "loading" ? (
                                <div className="offers_tnc_status">Loading terms...</div>
                            ) : null}
                            {termsState.status === "ready" ? (
                                <div
                                    className="offers_tnc_content"
                                    dangerouslySetInnerHTML={{ __html: termsState.html }}
                                />
                            ) : null}
                            {termsState.status === "error" ? (
                                <div className="offers_tnc_status">
                                    <strong>{termsState.error}</strong>
                                </div>
                            ) : null}
                        </div>
                        <div className="offers_tnc_modal_footer">
                            <button
                                type="button"
                                className="offers_tnc_open_page"
                                onClick={handleTermsPageOpen}
                                disabled={!hasTermsUrl}
                            >
                                <FiExternalLink aria-hidden="true" />
                                <span>Open Page</span>
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
};

const ScratchOfferCard = ({ offer, isRevealed, onReveal }) => {
    const canvasRef = useRef(null);
    const isScratchingRef = useRef(false);
    const isRevealSavingRef = useRef(false);
    const lastScratchPointRef = useRef(null);
    const scratchTravelRef = useRef(0);
    const scratchCloseTimerRef = useRef(null);
    const [isScratchPopupOpen, setIsScratchPopupOpen] = useState(false);
    const [isScratchPopupClosing, setIsScratchPopupClosing] = useState(false);

    useEffect(() => () => {
        if (scratchCloseTimerRef.current) {
            clearTimeout(scratchCloseTimerRef.current);
        }
    }, []);

    useEffect(() => {
        if (isRevealed) return undefined;

        const canvas = canvasRef.current;
        if (!canvas) return undefined;

        const drawCover = () => {
            const rect = canvas.getBoundingClientRect();
            const scale = window.devicePixelRatio || 1;
            const palette = SCRATCH_COVER_PALETTES[offer.tone] || SCRATCH_COVER_PALETTES.gold;

            canvas.width = Math.max(1, Math.floor(rect.width * scale));
            canvas.height = Math.max(1, Math.floor(rect.height * scale));

            const ctx = canvas.getContext("2d");
            ctx.setTransform(scale, 0, 0, scale, 0, 0);

            const gradient = ctx.createLinearGradient(0, 0, rect.width, rect.height);
            gradient.addColorStop(0, palette.start);
            gradient.addColorStop(0.5, palette.mid);
            gradient.addColorStop(1, palette.end);

            ctx.globalCompositeOperation = "source-over";
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, rect.width, rect.height);

            const drawGift = (x, y, size) => {
                const unit = size / 24;

                ctx.save();
                ctx.translate(x, y);
                ctx.lineWidth = Math.max(1.4, unit * 1.8);
                ctx.strokeStyle = palette.ink;
                ctx.lineCap = "round";
                ctx.lineJoin = "round";
                ctx.strokeRect(-9 * unit, -1 * unit, 18 * unit, 12 * unit);
                ctx.strokeRect(-7 * unit, -7 * unit, 14 * unit, 6 * unit);
                ctx.beginPath();
                ctx.moveTo(0, -7 * unit);
                ctx.lineTo(0, 11 * unit);
                ctx.moveTo(-9 * unit, 5 * unit);
                ctx.lineTo(9 * unit, 5 * unit);
                ctx.moveTo(-2 * unit, -7 * unit);
                ctx.bezierCurveTo(-10 * unit, -15 * unit, -14 * unit, -4 * unit, -4 * unit, -4 * unit);
                ctx.moveTo(2 * unit, -7 * unit);
                ctx.bezierCurveTo(10 * unit, -15 * unit, 14 * unit, -4 * unit, 4 * unit, -4 * unit);
                ctx.stroke();
                ctx.restore();
            };

            const drawSpark = (x, y, size) => {
                ctx.save();
                ctx.translate(x, y);
                ctx.strokeStyle = palette.ink;
                ctx.lineWidth = Math.max(1, size * 0.11);
                ctx.lineCap = "round";
                ctx.beginPath();
                ctx.moveTo(0, -size);
                ctx.lineTo(0, size);
                ctx.moveTo(-size, 0);
                ctx.lineTo(size, 0);
                ctx.moveTo(-size * 0.56, -size * 0.56);
                ctx.lineTo(size * 0.56, size * 0.56);
                ctx.moveTo(size * 0.56, -size * 0.56);
                ctx.lineTo(-size * 0.56, size * 0.56);
                ctx.stroke();
                ctx.restore();
            };

            const coverUnit = Math.min(rect.width, rect.height);
            const smallGift = Math.max(16, Math.min(coverUnit * 0.11, 26));
            const mediumGift = Math.max(22, Math.min(coverUnit * 0.14, 34));
            const heroGift = Math.max(42, Math.min(coverUnit * 0.24, 62));

            const marks = [
                [0.09, 0.17, "spark", 8], [0.24, 0.12, "gift", mediumGift], [0.43, 0.15, "spark", 6],
                [0.63, 0.12, "gift", smallGift], [0.84, 0.17, "spark", 8], [0.15, 0.43, "gift", smallGift],
                [0.34, 0.43, "spark", 7], [0.5, 0.39, "gift", heroGift], [0.71, 0.44, "spark", 6],
                [0.9, 0.42, "gift", mediumGift], [0.11, 0.76, "spark", 7], [0.28, 0.78, "gift", smallGift],
                [0.48, 0.78, "spark", 7], [0.69, 0.76, "gift", smallGift], [0.88, 0.78, "spark", 8],
            ];

            marks.forEach(([x, y, kind, size]) => {
                if (kind === "gift") {
                    drawGift(rect.width * x, rect.height * y, size);
                    return;
                }

                drawSpark(rect.width * x, rect.height * y, size);
            });

            ctx.fillStyle = palette.shadow;
            ctx.font = `900 ${Math.min(rect.width * 0.09, 30)}px Poppins, sans-serif`;
            ctx.textAlign = "center";
            ctx.textBaseline = "alphabetic";
            ctx.fillText("Nabhiverse", rect.width / 2, rect.height * 0.24);

            ctx.fillStyle = "rgba(255, 255, 255, 0.88)";
            ctx.font = `900 ${Math.min(rect.width * 0.12, 42)}px Poppins, sans-serif`;
            ctx.textBaseline = "middle";
            ctx.fillText("Scratch here", rect.width / 2, rect.height * 0.62);
        };

        drawCover();
        window.addEventListener("resize", drawCover);

        return () => {
            window.removeEventListener("resize", drawCover);
        };
    }, [isRevealed, isScratchPopupOpen, offer.tone]);

    const getCanvasPoint = (event) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();

        return {
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
        };
    };

    const closeScratchPopup = () => {
        if (!isScratchPopupOpen || isScratchPopupClosing) return;

        setIsScratchPopupClosing(true);
        isScratchingRef.current = false;
        lastScratchPointRef.current = null;
        scratchTravelRef.current = 0;

        scratchCloseTimerRef.current = setTimeout(() => {
            setIsScratchPopupOpen(false);
            setIsScratchPopupClosing(false);
        }, SCRATCH_POPUP_ANIMATION_MS);
    };

    const openScratchPopup = () => {
        if (scratchCloseTimerRef.current) {
            clearTimeout(scratchCloseTimerRef.current);
        }

        setIsScratchPopupClosing(false);
        setIsScratchPopupOpen(true);
        lastScratchPointRef.current = null;
        scratchTravelRef.current = 0;
    };

    const finishScratchReveal = () => {
        if (isScratchPopupClosing || isRevealSavingRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas?.getContext("2d");

        if (canvas && ctx) {
            ctx.save();
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.restore();
        }

        setIsScratchPopupClosing(true);
        isScratchingRef.current = false;
        isRevealSavingRef.current = true;
        lastScratchPointRef.current = null;
        scratchTravelRef.current = 0;

        scratchCloseTimerRef.current = setTimeout(async () => {
            const didReveal = await onReveal(offer);

            if (didReveal !== false) {
                setIsScratchPopupOpen(false);
            }

            isRevealSavingRef.current = false;
            setIsScratchPopupClosing(false);
        }, SCRATCH_POPUP_ANIMATION_MS);
    };

    const updateScratchProgress = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        let transparentPixels = 0;
        const sampleStep = 16;

        for (let index = 3; index < imageData.length; index += 4 * sampleStep) {
            if (imageData[index] < 40) transparentPixels += 1;
        }

        const sampledPixels = imageData.length / (4 * sampleStep);
        const nextProgress = Math.round((transparentPixels / sampledPixels) * 100);

        if (
            nextProgress >= SCRATCH_REVEAL_THRESHOLD &&
            scratchTravelRef.current >= SCRATCH_MIN_DRAG_DISTANCE
        ) {
            finishScratchReveal();
        }
    };

    const scratchAt = (event, shouldStartStroke = false) => {
        if (isRevealed) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        const { x, y } = getCanvasPoint(event);
        const rect = canvas.getBoundingClientRect();
        const radius = Math.max(18, Math.min(rect.width * 0.085, 27));
        const previousPoint = shouldStartStroke ? null : lastScratchPointRef.current;

        ctx.globalCompositeOperation = "destination-out";
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.lineWidth = radius * 2;

        if (previousPoint) {
            const distance = Math.hypot(x - previousPoint.x, y - previousPoint.y);
            if (distance < 2) return;

            scratchTravelRef.current += distance;
            ctx.beginPath();
            ctx.moveTo(previousPoint.x, previousPoint.y);
            ctx.lineTo(x, y);
            ctx.stroke();
        }

        if (!previousPoint) {
            lastScratchPointRef.current = { x, y };
            return;
        }

        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
        lastScratchPointRef.current = { x, y };
        updateScratchProgress();
    };

    const handlePointerDown = (event) => {
        if (!isScratchPopupOpen) {
            openScratchPopup();
            return;
        }

        isScratchingRef.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
        scratchAt(event, true);
    };

    const handlePointerMove = (event) => {
        if (!isScratchingRef.current) return;
        scratchAt(event);
    };

    const stopScratching = () => {
        isScratchingRef.current = false;
        lastScratchPointRef.current = null;
    };

    if (!isRevealed) {
        return (
            <>
                <article
                    className={`offers_scratch_card offers_scratch_card_locked tone_${offer.tone}`}
                    onClick={openScratchPopup}
                >
                    <div className="offers_scratch_zone offers_scratch_only">
                        <div className="offers_locked_result">
                            <OfferRewardPanel offer={offer} />
                        </div>
                        <canvas
                            ref={isScratchPopupOpen ? null : canvasRef}
                            className="offers_scratch_canvas offers_scratch_canvas_preview"
                            onPointerDown={handlePointerDown}
                            aria-label="Open scratch card"
                        />
                    </div>
                </article>

                {isScratchPopupOpen ? (
                    <div
                        className={`offers_scratch_modal ${isScratchPopupClosing ? "is_closing" : ""}`}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Scratch card reward"
                    >
                        <div className="offers_scratch_modal_box" onClick={(event) => event.stopPropagation()}>
                            <button
                                className="offers_scratch_modal_close"
                                type="button"
                                aria-label="Close scratch card"
                                onClick={closeScratchPopup}
                            >
                                x
                            </button>
                            <div className="offers_scratch_modal_copy">
                                <span>Congratulations</span>
                                <strong>You have won a scratch card</strong>
                            </div>
                            <article className={`offers_scratch_card offers_scratch_card_locked tone_${offer.tone}`}>
                                <div className="offers_scratch_zone offers_scratch_only">
                                    <div className="offers_locked_result">
                                        <OfferRewardPanel offer={offer} />
                                    </div>
                                    <canvas
                                        ref={canvasRef}
                                        className="offers_scratch_canvas"
                                        onPointerDown={handlePointerDown}
                                        onPointerMove={handlePointerMove}
                                        onPointerUp={stopScratching}
                                        onPointerCancel={stopScratching}
                                        onPointerLeave={stopScratching}
                                        aria-label="Scratch to reveal hidden offer"
                                    />
                                </div>
                            </article>
                        </div>
                    </div>
                ) : null}
            </>
        );
    }

    return (
        <article className={`offers_scratch_card offers_scratch_card_revealed tone_${offer.tone}`}>
            <div className={`offers_scratch_zone ${isRevealed ? "is_revealed" : ""}`}>
                <OfferRewardPanel offer={offer} />
            </div>
        </article>
    );
};

const Offers = () => {
    const [revealedOffers, setRevealedOffers] = useState([]);
    const [offerItems, setOfferItems] = useState();
    const [isLoading, setIsLoading] = useState(false);

    const getHubbleRequestConfig = () => {
        const apiToken = getXsrfTokenCookie();
        const config = {
            headers: {},
        };

        if (apiToken) {
            config.headers.Authorization = `Bearer ${apiToken}`;
        }

        return config;
    };

    useEffect(() => {
        window.scrollTo(0, 0);

        const fetchOfferCards = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get(
                    `${SHOP_API_BASE_URL}customer/hubble/offers-vouchers`,
                    getHubbleRequestConfig()
                );

                const offers = response?.data?.offers || [];
                const cards = offers.flatMap((offerData) =>
                    (offerData.vouchers || []).map((voucher, index) =>
                        mapApiVoucherToOfferCard(offerData, voucher, index)
                    )
                ).map((card, index) => ({
                    ...card,
                    tone: OFFER_TONES[index % OFFER_TONES.length],
                }));

                if (cards.length > 0) {
                    setOfferItems(cards);
                    setRevealedOffers(cards.filter((card) => card.isScratched).map((card) => card.id));
                }
            } catch (error) {
                console.error("Failed to load Hubble offer vouchers:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchOfferCards();
    }, []);

    const handleReveal = (offer) => {
        const offerId = typeof offer === "string" ? offer : offer?.id;
        const scratchId = typeof offer === "string" ? offer : offer?.scratchId;

        if (!offerId) return false;

        if (revealedOffers.includes(offerId)) {
            return true;
        }

        setRevealedOffers((currentOffers) =>
            currentOffers.includes(offerId) ? currentOffers : [...currentOffers, offerId]
        );
        setOfferItems((currentItems) =>
            currentItems?.map((currentOffer) =>
                currentOffer.id === offerId ? { ...currentOffer, isScratched: true } : currentOffer
            )
        );

        if (scratchId) {
            axios.post(
                `${SHOP_API_BASE_URL}customer/hubble/vouchers/${scratchId}/skarch`,
                undefined,
                getHubbleRequestConfig()
            )
                .catch((error) => {
                    console.error("Failed to scratch Hubble voucher:", error);
                    showTost("Voucher revealed, but scratch status could not be saved.", "error");
                });
        }

        return true;
    };

    const unlockedCount = revealedOffers.length;

    return (
        <ThemedPageFrame
            activePath="/offers"
            eyebrow="Gift Cards & Vouchers"
            title="Offers"
            subtitle="Scratch each card to reveal gift cards, vouchers, cashback rewards, and redeemable offer codes."
            summary={<><span>Unlocked</span><strong>{unlockedCount}/{offerItems?.length}</strong></>}
            className="offers_page_layout"
        >
            <div className="offers_page_shell">
                <section className="offers_feature_band">
                    <div className="offers_feature_copy">
                        <span><BsStars /> Scratch rewards</span>
                        <h2>Reveal handpicked rewards before checkout</h2>
                        <p>Each voucher starts hidden, just like a payment reward card. Scratch, unlock the code, then redeem it for your next Nabhiverse booking, wallet payment, dining, or shopping order.</p>
                    </div>
                    <div className="offers_feature_stats" aria-label="Offer summary">
                        <div>
                            <strong>{offerItems?.length}</strong>
                            <span>Live offers</span>
                        </div>
                        <div>
                            <strong>{unlockedCount}</strong>
                            <span>Revealed</span>
                        </div>
                        <div>
                            <FiCheckCircle />
                            <span>Ready to redeem</span>
                        </div>
                    </div>
                </section>

                <section className="offers_grid" aria-label="Gift cards and vouchers">
                    {isLoading ? (
                        <div className="offers_loading_message">Loading offers…</div>
                    ) : offerItems?.length ? (
                        offerItems?.map((offer) => (
                            <ScratchOfferCard
                                key={offer.id}
                                offer={offer}
                                isRevealed={revealedOffers.includes(offer.id)}
                                onReveal={handleReveal}
                            />
                        ))
                    ) : (
                        <div className="offers_loading_message">No offers available at the moment.</div>
                    )}
                </section>
            </div>
        </ThemedPageFrame>
    );
};

export default Offers;
/* jshint ignore:end */
